import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';

import { format } from 'date-fns';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { QUERY_FILTER_OPTIONS } from 'app/hub-components/constants/query-filter.constants';
import { FilterSource } from 'app/hub-components/models/filter-source.model';
import { DatesService } from 'app/hub-components/modules/date-range-picker/dates.service';
import { DynamicDateRange } from './dynamic-date-range.model';
import { DynamicDate } from './dynamic-date.model';
import { IQueryFilterWrapper, IQueryOptions } from '../../../../interfaces/query-filter.interface';
import { Query } from '../../../../models/analytics-api/query.model';
import { Dimension, Metric, Schema } from '../../../../models/analytics-api/schema.model';
import { SearchResult } from '../../../../models/analytics-api/search_result.model';
import { QueryFilter } from '../../../../models/query-filter.model';
import { QueryFilterOption } from '../../../../models/query-option.model';
import { AnalyticsApiService } from '../../../../services/analytics-api/analytics-api.service';
import { QueryService } from '../../../../services/query/query.service';
import { TrackingService } from '../../../../services/tracking/tracking.service';

@Component({
  selector: 'query-filters',
  templateUrl: './query-filters.component.html',
  styleUrls: ['./query-filters.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class QueryFiltersComponent implements OnInit, OnDestroy, OnChanges {
  @Input() datasetName: string;
  @Input() schema: Schema;
  @Input() filters: IQueryFilterWrapper[] = [];
  @Input() interval: string;
  @Input() showDefaultFilter: boolean = false;
  @Input() readonly: boolean = false;
  @Input() externalFilterSources: FilterSource[] = [];
  @Input() externalMetrics: Metric[] = [];
  @Input() filterOptions: QueryFilterOption[] = [];
  @Input() ignoreFilterSources: string[] = [];
  @Input() options: IQueryOptions = {
    showMultipleOptions: true,
    showOrFilters: true,
    showEmptyFilterWarning: true,
  };

  @Input() supportYearmonth: boolean = false;
  @Input() supportDate: boolean = false;
  @Input() supportRelativeDates: boolean = true;
  @Input() firstDayOfWeek: string;

  @Output() filtersData: EventEmitter<{ filters: IQueryFilterWrapper[], emptyFilterTypeWarnings: boolean[][] }> = new EventEmitter();

  public editableFilterOptions: any[][] = [];
  public queryFilterSelection: string[][] = [];
  public filterSourceWarnings: boolean[][] = [];
  public query: Query;
  public filterSourceList: FilterSource[];
  public editableFilterSources: FilterSource[];
  public loading: boolean[][] = [];
  public emptyFilterValueWarnings: boolean[][] = [];
  public emptyFilterTypeWarnings: boolean [][] = [];
  public dateValues: (Date | DynamicDate | DynamicDateRange | null)[][] = [];
  public dynamicDateDirectionOptions = [
    { label: 'ago', value: 'ago' },
    { label: 'from now', value: 'from-now' },
  ];

  public dateTypeOptions = [
    { label: 'Static', icon: 'fa-solid fa-calendar-day', value: 'static' },
    { label: 'Relative', icon: 'fa-solid fa-clock', value: 'dynamic' },
  ];

  public selectedDateTypes: string[][] = [];

  public dateRangeRelationOptions = [
    { label: 'Last', value: 'last' },
    { label: 'This', value: 'this' },
    { label: 'Next', value: 'next' },
  ];

  public dateRangeUnitOptions = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Quarter', value: 'quarter' },
    { label: 'Year', value: 'year' },
  ];

  public dropdownMultiSettings: any = {
    removeAll: false,
    field: 'slice',
  };

  public dropdownSingleSettings: any = {
    fields: {
      value: 'key',
      name: 'displayName',
    },
  };

  public filtersArray: IQueryFilterWrapper[] = [];
  public matches: any[] = [];
  private matches$: Subject<any> = new Subject<any>();
  private searchSubject$: Subject<any> = new Subject<any>();
  private _matches: any[] = [];
  private externalIncompatibleFilterSources: string[] = [];
  private analyticsSubscription: Subscription[][] = [];
  private editableDimensionsSubscription: Subscription;

  @ViewChild('filterInput') filterInput: ElementRef;

  constructor(
    private analyticsApi: AnalyticsApiService,
    private queryService: QueryService,
    private cdr: ChangeDetectorRef,
    private trackingService: TrackingService,
    private datesService: DatesService,
  ) { }

  public ngOnInit(): void {
    this.firstDayOfWeek = this.firstDayOfWeek || this.datesService.accountFirstDayOfWeek.toString();
    this.initSubscriptions();
    this.configIncomingSelected();
    this.configDefaults();
    this.initDefaults();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      (
        changes['datasetName'] && !changes['datasetName'].isFirstChange() &&
        changes['schema'] && !changes['schema'].isFirstChange()
      ) ||
      (!!changes.readonly && !changes.readonly.isFirstChange() && !changes.readonly.currentValue)
    ) {
      this.resetDefaults();
      this.configDefaults();
      this.initDefaults();
    }

    if ((!!changes.externalMetrics && !changes.externalMetrics.isFirstChange()) ||
      (!!changes.externalFilterSources && !changes.externalFilterSources.isFirstChange())) {
      this.updateEditableFilterSources();
      this.recheckAllFilterWarnings();
    }
  }

  public dropdownSearch(searchTerm: string, queryFilter: QueryFilter, indexAnd: number, indexOr: number): void {
    const selectedFilterSource = this.findFilterSource(queryFilter);
    if (this._matches[selectedFilterSource.key]) {
      this.cancelSubscription(this.analyticsSubscription[indexAnd][indexOr]);
    }

    if (searchTerm) {
      this.matches[selectedFilterSource.key] = [];
      this.analyticsSearch(selectedFilterSource, searchTerm, indexAnd, indexOr);
    } else {
      this.matches[selectedFilterSource.key] = this._matches[selectedFilterSource.key];
      this.loading[indexAnd][indexOr] = false;
    }
    this.cdr.detectChanges();
  }

  public onFilterInputChange(value: string, filter: QueryFilter, indexAnd: number, indexOr: number) {
    this.loading[indexAnd][indexOr] = true;
    this.searchSubject$.next({ searchTerm: value, filter: filter, indexAnd: indexAnd, indexOr: indexOr });
  }

  public changeFilterSource(filterSource: FilterSource, indexAnd: number, indexOr: number, resetValues: boolean = false): void {
    if (!filterSource) {
      return;
    }

    if (resetValues) {
      this.filtersArray[indexAnd].expressions[indexOr].values = [];
    }

    this.dateValues[indexAnd][indexOr] = null;
    this.selectedDateTypes[indexAnd][indexOr] = 'static';

    if (!this.filtersArray[indexAnd]) {
      this.filtersArray[indexAnd] = { expressions: [] as QueryFilter[] };
    }

    if (!this.filtersArray[indexAnd].expressions[indexOr]) {
      this.filtersArray[indexAnd].expressions[indexOr] = {} as QueryFilter;
    }

    this.filtersArray[indexAnd].expressions[indexOr].key = filterSource.key;
    this.filtersArray[indexAnd].expressions[indexOr]['dimension'] = filterSource.dimension;
    this.filtersArray[indexAnd].expressions[indexOr]['dimensionProperty'] = filterSource.dimensionProperty;

    this.setEditableFilterOptions(filterSource, indexAnd, indexOr, resetValues);
    this.updateEditableFilterSources();
    this.checkFilterWarningStatus(indexAnd, indexOr, filterSource);
    this.checkEmptyFilterTypeWarnings(indexAnd, indexOr);

    this.filtersArray[indexAnd].expressions[indexOr].isSearchable = filterSource.isSearchable;

    this.updateQueryFilters();
  }

  public changeFilterType(filterType: QueryFilterOption, blockIdx: number, filterIdx: number, resetValues?: boolean) {
    this.filtersArray[blockIdx].expressions[filterIdx].type = filterType?.type;
    this.queryFilterSelection[blockIdx][filterIdx] = filterType?.type;
    if (resetValues) {
      this.filtersArray[blockIdx].expressions[filterIdx].values = [];
    }

    if (filterType?.type === QUERY_FILTER_OPTIONS.any.type || filterType?.type === QUERY_FILTER_OPTIONS.not_any.type) {
      const filterSource = this.findFilterSource(this.filtersArray[blockIdx].expressions[filterIdx]);
      if (filterSource.isSearchable && !this._matches[filterSource?.key] && !this.readonly) {
        this.changeDataset(filterSource, blockIdx, filterIdx);
      }
    }

    this.checkEmptyFilterTypeWarnings(blockIdx, filterIdx);
    this.updateQueryFilters();
  }

  public addFilter(indexAnd: number, indexOr: number): void {
    this.trackingService.track('Add OR block to advanced filter');
    this.filtersArray[indexAnd].expressions.push(this.defaultFilter);
    this.changeFilterSource(this.filterSourceList[0], indexAnd, indexOr + 1);
  }

  public addBlock(): void {
    this.trackingService.track('Add AND block to advanced filter');
    const newIndex = this.filtersArray.length;
    this.filtersArray.push({ expressions: [this.defaultFilter] });

    this.initArrays(newIndex);
    this.changeFilterSource(this.filterSourceList[0], newIndex, 0);
  }

  public removeFilter(indexAnd: number, indexOr: number): void {
    if (this.filtersArray[indexAnd].expressions.length === 1) {
      this.trackingService.track('Remove AND block from advanced filter');
      this.filtersArray.splice(indexAnd, 1);
      this.queryFilterSelection.splice(indexAnd, 1);
      this.emptyFilterValueWarnings.splice(indexAnd, 1);
      this.emptyFilterTypeWarnings.splice(indexAnd, 1);
      this.dateValues.splice(indexAnd, 1);
      this.selectedDateTypes.splice(indexAnd, 1);
    } else {
      this.trackingService.track('Remove OR block from advanced filter');
      this.filtersArray[indexAnd].expressions.splice(indexOr, 1);
      this.queryFilterSelection[indexAnd].splice(indexOr, 1);
      this.emptyFilterValueWarnings[indexAnd].splice(indexOr, 1);
      this.emptyFilterTypeWarnings[indexAnd].splice(indexOr, 1);
      this.dateValues[indexAnd].splice(indexOr, 1);
      this.selectedDateTypes[indexAnd].splice(indexOr, 1);
    }
    this.updateQueryFilters();
    this.updateEditableFilterSources();
  }

  public removeSelectedFilterValue(event: MouseEvent, filterValue: { slice: string, value: string }, indexAnd: number, indexOr: number) {
    event.stopPropagation();
    const filterValues = this.filtersArray[indexAnd].expressions[indexOr].values;

    if (Array.isArray(filterValues)) {
      this.filtersArray[indexAnd].expressions[indexOr].values = filterValues.filter(
        value => value.slice !== filterValue.slice,
      );
    }
  }

  public onValueChange(value: any, indexAnd: number, indexOr: number) {
    if (this.isDateDimension(this.filtersArray[indexAnd].expressions[indexOr])) {
      let dateString = null;
      if (value instanceof Date) {
        dateString = format(value, 'yyyy-MM-dd');
        this.selectedDateTypes[indexAnd][indexOr] = 'static';
      } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        dateString = value;
        value = this.datesService.parseSingleDateValue(value);
        this.selectedDateTypes[indexAnd][indexOr] = 'static';
      } else if (typeof value === 'string' && DynamicDate.STRING_REGEX.test(value)) {
        dateString = value;
        value = DynamicDate.parse(value);
        this.selectedDateTypes[indexAnd][indexOr] = 'dynamic';
      } else if (typeof value === 'string' && DynamicDateRange.STRING_REGEX.test(value)) {
        value = DynamicDateRange.parse(value);
        dateString = value.toString(this.firstDayOfWeek);
      } else if (value instanceof DynamicDate) {
        dateString = value.toString();
        this.selectedDateTypes[indexAnd][indexOr] = 'dynamic';
      } else if (value instanceof DynamicDateRange) {
        dateString = value.toString(this.firstDayOfWeek);
      } else {
        value = null;
      }
      const dateFilterType = value instanceof DynamicDateRange ? 'dynamic range' : this.selectedDateTypes[indexAnd][indexOr];
      this.trackingService.track(`Add/modify ${dateFilterType} date filter value in advanced filter`);
      this.filtersArray[indexAnd].expressions[indexOr].values = dateString;
      this.dateValues[indexAnd][indexOr] = value;
      if (this.options.showEmptyFilterWarning) {
        this.emptyFilterValueWarnings[indexAnd][indexOr] = !value;
      }
    } else if (this.options.showEmptyFilterWarning) {
      this.emptyFilterValueWarnings[indexAnd][indexOr] = !value?.length;
    }
  }

  public onDynamicDateChange(change: { num?: number; direction?: 'ago' | 'from-now' }, indexAnd: number, indexOr: number) {
    let dynamicDate = this.getDateValue(this.filtersArray[indexAnd].expressions[indexOr], indexAnd, indexOr);
    if (!(dynamicDate instanceof DynamicDate)) {
      dynamicDate = new DynamicDate(undefined, 'days', 'ago');
    }
    if ('num' in change) {
      dynamicDate.num = change.num;
    }
    if ('direction' in change) {
      dynamicDate.direction = change.direction;
    }
    dynamicDate.unit = 'days';
    this.onValueChange(dynamicDate, indexAnd, indexOr);
  }

  public onDynamicDateRangeChange(change: { relation?: 'last' | 'this' | 'next', unit?: 'week' | 'month' | 'quarter' | 'year' }, indexAnd: number, indexOr: number) {
    let dynamicDateRange = this.getDateValue(this.filtersArray[indexAnd].expressions[indexOr], indexAnd, indexOr);
    if (!(dynamicDateRange instanceof DynamicDateRange)) {
      dynamicDateRange = new DynamicDateRange('last', 'week');
    }
    if ('relation' in change) {
      dynamicDateRange.relation = change.relation;
    }
    if ('unit' in change) {
      dynamicDateRange.unit = change.unit;
    }
    this.onValueChange(dynamicDateRange, indexAnd, indexOr);
  }

  public getDateValue(filter: QueryFilter, indexAnd: number, indexOr: number): Date | DynamicDate | DynamicDateRange | null {
    if (!this.dateValues[indexAnd][indexOr] && filter.values && typeof filter.values === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(filter.values)) {
        this.dateValues[indexAnd][indexOr] = this.datesService.parseSingleDateValue(filter.values);
        this.selectedDateTypes[indexAnd][indexOr] = 'static';
      } else if (DynamicDate.STRING_REGEX.test(filter.values)) {
        this.dateValues[indexAnd][indexOr] = DynamicDate.parse(filter.values);
        this.selectedDateTypes[indexAnd][indexOr] = 'dynamic';
      } else if (DynamicDateRange.STRING_REGEX.test(filter.values)) {
        this.dateValues[indexAnd][indexOr] = DynamicDateRange.parse(filter.values);
      }
    } else if (!this.dateValues[indexAnd][indexOr] && (filter.type === 'is_in' || filter.type === 'not_is_in')) {
      const defaultRange = new DynamicDateRange('last', 'week');
      this.dateValues[indexAnd][indexOr] = defaultRange;
      this.filtersArray[indexAnd].expressions[indexOr].values = defaultRange.toString(this.firstDayOfWeek);
    }
    return this.dateValues[indexAnd][indexOr];
  }

  public findFilterSource(filter: QueryFilter) {
    return this.queryService.findFilterSource(filter, this.filterSourceList);
  }

  public findQueryFilterOption(filter: string) {
    if (filter) {
      return this.filterOptions.find(option => option.type === filter);
    }
  }

  public trackByFiltersArray(index: number, _item: any) {
    return 'block-' + index;
  }

  public trackByFiltersExpressions(indexAnd: number, indexOr: number, item: any) {
    return item.key + '-' + item.type + '-' + indexAnd + '-' + indexOr;
  }

  public onFilterSourceDropdown(indexAnd: number, indexOr: number) {
    this.updateEditableFilterSources(indexAnd, indexOr);
  }

  public multiSelectDropdownClick(filter: QueryFilter, indexAnd: number, indexOr: number) {
    const filterSource = this.findFilterSource(filter);

    if (!this._matches[filterSource?.key] && !this.readonly) {
      this.matches[filterSource?.key] = [];
      this.changeDataset(filterSource, indexAnd, indexOr);
    } else {
      this.dropdownSearch('', filter, indexAnd, indexOr);
    }
  }

  public focusSearch(): void {
    if (this.filterInput?.nativeElement) {
      this.filterInput.nativeElement.focus();
    }
  }

  public ngOnDestroy(): void {
    if (this.matches$) {
      this.matches$ && this.matches$.complete();
    }
    if (this.editableDimensionsSubscription) {
      this.editableDimensionsSubscription.unsubscribe();
    }
    if (this.searchSubject$) {
      this.searchSubject$?.complete();
    }
  }

  private analyticsSearch(filterSource: FilterSource, searchTerm: string, indexAnd?: number, indexOr?: number) {
    const interval = this.datesService.intervalToFixedDateRange(this.interval);
    if (!this.datesService.isValid(interval.startDate, interval.endDate)) {
      this.loading[indexAnd][indexOr] = false;
      return;
    }

    if (!filterSource.isDimensionProperty && !['yearmonth', 'date'].includes(filterSource.dimension.name)) {
      this.analyticsSubscription[indexAnd][indexOr] =
        this.analyticsApi
          .search(this.query, filterSource.dimension, searchTerm, { cache: false, skipLoader: true }, 50)
          .pipe(
            take(1),
          )
          .subscribe((result: SearchResult) => {
            const matchesResult = result.matches.map(match => match.toJson());
            this.matches$.next({ matches: matchesResult, key: filterSource.key });
            this.loading[indexAnd][indexOr] = false;
            this.cdr.markForCheck();
          });
    }
  }

  private configDefaults() {
    this.filtersArray = this.processFilters(this.filters);
    let allowedSchemaDimensions = this.schema.dimensions?.filter(d => !this.ignoreFilterSources.includes(d.name));
    this.filterSourceList = this.queryService.getFilterSourceList(allowedSchemaDimensions);

    if (this.supportYearmonth) {
      this.filterSourceList.unshift(new FilterSource(new Dimension('yearmonth', null, null, true)));
    }
    if (this.supportDate) {
      this.filterSourceList.unshift(new FilterSource(new Dimension('date', null, null, true)));
    }

    this.configDefaultQuery();

    this.filters.forEach((blocks, blockIdx) => {
      this.initArrays(blockIdx);
      blocks.expressions.forEach((filter, filterIdx) => {
        const filterSource = this.findFilterSource(filter);
        if (!filterSource) {
          return;
        }
        this.changeFilterSource(filterSource, blockIdx, filterIdx);
        this.setEditableFilterOptions(filterSource, blockIdx, filterIdx);
        this.onValueChange(filter.values, blockIdx, filterIdx);
        this.checkFilterWarningStatus(blockIdx, filterIdx, filterSource);
        this.queryFilterSelection[blockIdx][filterIdx] = filter.type;

        if (filter.type === 'any' || filter.type === 'not_any') {
          this.matches[filter.key] = [{ slice: null, value: null }];
        }
      });
    });
  }

  private setEditableFilterOptions(filterSource: FilterSource, indexAnd: number, indexOr: number, reset?: boolean) {
    if (filterSource.dimension.name === 'yearmonth' || this.isDateDimension(this.filtersArray[indexAnd].expressions[indexOr])) {
      this.editableFilterOptions[indexAnd][indexOr] = this.filterOptions.filter(o => o.type === 'gt' || o.type === 'gte' || o.type === 'lt' || o.type === 'lte' || o.type === 'eq' || o.type === 'not_eq' || (this.supportRelativeDates && (o.type === 'is_in' || o.type === 'not_is_in')));
    } else if (filterSource.isDimensionProperty) {
      this.editableFilterOptions[indexAnd][indexOr] = this.filterOptions.filter(o => o.type !== 'any' && o.type !== 'not_any' && o.type !== 'is_in' && o.type !== 'not_is_in');
    } else {
      this.editableFilterOptions[indexAnd][indexOr] = this.filterOptions.filter(o => o.type !== 'is_in' && o.type !== 'not_is_in');
    }

    if (reset) {
      if (filterSource.dimension.name === 'yearmonth' || this.isDateDimension(this.filtersArray[indexAnd].expressions[indexOr])) {
        const index = this.filterOptions.findIndex(o => o.type === 'eq');
        this.changeFilterType(this.filterOptions[index], indexAnd, indexOr);
      } else {
        this.changeFilterType(null, indexAnd, indexOr);
      }
    }
  }

  private configIncomingSelected() {
    this.externalIncompatibleFilterSources = this.externalFilterSources.reduce((incompatibleFilterSources: string[], source: FilterSource) => {
      const data = this.queryService.getIncompatibleFilterSources([source], this.schema);
      incompatibleFilterSources.push(...data);
      return incompatibleFilterSources;
    }, []);
  }

  private recheckAllFilterWarnings() {
    this.configIncomingSelected();
    this.filtersArray.forEach((block, indexAnd) => {
      block.expressions.forEach((filter, indexOr) => {
        this.checkFilterWarningStatus(indexAnd, indexOr, this.findFilterSource(filter));
      });
    });
  }

  private checkFilterWarningStatus(blockIdx: number, filterIdx: number, filter: FilterSource) {
    this.filterSourceWarnings[blockIdx][filterIdx] = this.externalIncompatibleFilterSources.includes(filter.name);
  }

  private checkEmptyFilterTypeWarnings(indexAnd: number, indexOr: number): void {
    this.emptyFilterTypeWarnings[indexAnd][indexOr] = !this.filtersArray[indexAnd].expressions[indexOr].type;
  }

  private initDefaults() {
    if (this.showDefaultFilter && !this.filtersArray.length) {
      this.addBlock();
    }
  }

  private processFilters(data) {
    const formatted = JSON.parse(JSON.stringify(data));
    return this.queryService.processFilters(formatted);
  }

  private configDefaultQuery() {
    const DEFAULT_QUERY_JSON = {
      dataset: this.datasetName,
      interval: this.interval,
      dimensions: [],
    };
    this.query = new Query(DEFAULT_QUERY_JSON, this.schema);
  }

  private updateEditableFilterSources(indexAndToExclude?: number, indexOrToExclude?: number): void {
    const totalFilterSources = this.getFilterSources(indexAndToExclude, indexOrToExclude);
    const totalFilterSourceKeys = new Set(totalFilterSources.map(source => source.key));
    for (const externalFilterSource of this.externalFilterSources) {
      if (!totalFilterSourceKeys.has(externalFilterSource.key)) {
        totalFilterSources.push(externalFilterSource);
        totalFilterSourceKeys.add(externalFilterSource.key);
      }
    }

    this.queryService.updateEditableDimensions(totalFilterSources, this.schema, this.externalMetrics);
  }

  private getFilterSources(indexAndToExclude?: number, indexOrToExclude?: number) {
    const sources: FilterSource[] = [];
    const keys = new Set<string>();
    for (let indexAnd = 0; indexAnd < this.filtersArray.length; indexAnd++) {
      const expressions = this.filtersArray[indexAnd].expressions || [];
      for (let indexOr = 0; indexOr < expressions.length; indexOr++) {
        if (indexAnd === indexAndToExclude && indexOr === indexOrToExclude) {
          continue;
        }
        const expression = expressions[indexOr];
        if (!expression?.dimension) {
          continue;
        }
        const filterSource = new FilterSource(expression.dimension, expression.dimensionProperty);
        if (!keys.has(filterSource.key)) {
          keys.add(filterSource.key);
          sources.push(filterSource);
        }
      }
    }
    return sources;
  }

  private changeDataset(filterSource: FilterSource, indexAnd: number, indexOr: number): void {
    this.loading[indexAnd][indexOr] = true;
    this.cancelSubscription(this.analyticsSubscription[indexAnd][indexOr]);
    this.analyticsSearch(filterSource, null, indexAnd, indexOr);
  }

  private initSubscriptions(): void {
    this.matches$.subscribe(({ matches, key }) => {
      if (this._matches[key]) {
        this.matches[key] = matches;
      } else {
        this._matches[key] = matches;
        this.matches[key] = this._matches[key];
      }
    });

    this.editableDimensionsSubscription =
      this.queryService.getEditableDimensions().subscribe(dimensions => {
        this.editableFilterSources = this.queryService.getFilterSourceList(dimensions);
        if (this.supportYearmonth) {
          this.editableFilterSources.unshift(new FilterSource(new Dimension('yearmonth', null, null, true)));
        }
        if (this.supportDate) {
          this.editableFilterSources.unshift(new FilterSource(new Dimension('date', null, null, true)));
        }
      });

    this.searchSubject$
      .pipe(
        debounceTime(500),
      )
      .subscribe(payload => {
        this.dropdownSearch(payload.searchTerm, payload.filter, payload.indexAnd, payload.indexOr);
      });
  }

  private updateQueryFilters() {
    this.queryService.updateQueryFilters(this.filtersArray);
    this.filtersData.emit({ filters: this.filtersArray, emptyFilterTypeWarnings: this.emptyFilterTypeWarnings });
  }

  private get defaultFilter(): QueryFilter {
    return new QueryFilter({
      type: null,
      key: this.editableFilterSources[0]?.key,
      values: [],
      is_searchable: null,
      dimension: this.editableFilterSources[0]?.dimension,
    });
  }

  private initArrays(index) {
    this.analyticsSubscription[index] = [];
    this.loading[index] = [];
    this.queryFilterSelection[index] = [];
    this.filterSourceWarnings[index] = [];
    this.emptyFilterValueWarnings[index] = [];
    this.emptyFilterTypeWarnings[index] = [];
    this.editableFilterOptions[index] = [];
    this.dateValues[index] = [];
    this.selectedDateTypes[index] = [];
  }

  private resetDefaults() {
    this.filtersArray = [];
    this.filterSourceList = [];
    this.queryFilterSelection = [];
    this.filterSourceWarnings = [];
    this.emptyFilterValueWarnings = [];
    this.emptyFilterTypeWarnings = [];
    this._matches = [];
    this.matches = [];
    this.dateValues = [];
    this.selectedDateTypes = [];
  }

  private cancelSubscription(subscription: Subscription) {
    subscription?.unsubscribe();
  }

  public isDateDimension(filter: QueryFilter): boolean {
    const filterSource = this.findFilterSource(filter);
    const dateFormattedDimension = filterSource?.dimension?.meta?.display?.format?.format === 'date';
    const dateFormattedDimensionProperty = filterSource?.dimensionProperty?.meta?.display?.format?.format === 'date';
    return filterSource?.name?.toLowerCase() === 'date' || (!filterSource?.dimensionProperty && dateFormattedDimension) || dateFormattedDimensionProperty;
  }
}
